import React, { ReactNode } from 'react'
import { Stack, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { BottomDrawer } from '$common/bottom-drawer'
import { PrimaryButton } from '$common/buttons'
import { SecondaryOutlinedButton } from '$common/buttons/secondary-outlined-button'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { ArrowIOSIcon } from '$common/icons'

interface BoxProps {
  promptMode: boolean
}
const StyledBox = styled('div')<BoxProps>(({ promptMode }) => ({
  border: '1px solid #0000000d',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '8px',
  background: promptMode
    ? 'linear-gradient(0deg, rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05)),linear-gradient(0deg, rgba(85, 166, 255, 0.1), rgba(85, 166, 255, 0.1))'
    : 'linear-gradient(0deg, rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05)),linear-gradient(0deg, rgba(230, 73, 79, 0.1), rgba(230, 73, 79, 0.1))',
}))

const ActionRoot = ({
  promptMode,
  children,
}: {
  promptMode: boolean
  children: ReactNode
}) => {
  if (promptMode)
    return (
      <Stack direction="row" gap="12px" alignItems="end">
        {children}
      </Stack>
    )
  return <>{children}</>
}

export interface Props {
  title: string
  description: string
  promptMode: boolean
  onFirstAction: () => unknown
  onSecondAction?: () => unknown
  icon: React.ReactElement
  firstActionText: string
  secondActionText?: string
  isOpen?: boolean
}

export function PermissionDialog({
  title,
  description,
  promptMode,
  onFirstAction,
  onSecondAction,
  icon,
  firstActionText,
  secondActionText,
  isOpen,
}: Props) {
  const isDesktop = useIsDesktopSize()

  return (
    <BottomDrawer isOpen={isOpen!} onClose={closeCurrentModal}>
      <Stack
        sx={{ padding: isDesktop ? '16px' : '20px', boxSizing: 'border-box' }}
      >
        <StyledBox
          promptMode={promptMode}
          style={{ height: '160px', width: '100%' }}
        >
          {icon}
        </StyledBox>

        <Typography
          marginTop="20px"
          variant="subtitle1"
          dir="auto"
          sx={{
            overflowWrap: 'break-word',
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            textAlign: 'right',
          }}
          color="secondary700"
        >
          {title}
        </Typography>

        <Typography
          marginTop="8px"
          variant="body2"
          dir="auto"
          sx={{
            overflowWrap: 'break-word',
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            textAlign: 'right',
          }}
        >
          {description}
        </Typography>

        <ActionRoot promptMode={promptMode}>
          <PrimaryButton
            fullWidth
            text={firstActionText}
            onClick={onFirstAction}
            sx={{ marginTop: '20px' }}
          />

          {secondActionText && (
            <SecondaryOutlinedButton
              fullWidth
              text={secondActionText}
              onClick={onSecondAction}
              sx={{
                borderColor: promptMode ? '#81818180' : 'secondary.main',
                color: promptMode ? 'gray' : 'secondary.main',
                marginTop: '12px',
              }}
              endIcon={promptMode ? null : <ArrowIOSIcon />}
            />
          )}
        </ActionRoot>
      </Stack>
    </BottomDrawer>
  )
}

PermissionDialog.defaultProps = {
  secondActionText: '',
  isOpen: false,
}
